/**
 * home.ts
 * Home page data and Home CTA buttons cache management.
 */

import type { HomePageData } from '../homeContent';
import { cache, homeInFlight, isStale, notify, ALL_LANGS } from './base';

const LS_HOME_PREFIX = 'sargo_home_';
const LS_CTA_PREFIX = 'sargo_cta_';

interface StoredHomeEntry {
  data: HomePageData;
  fetchedAt: number;
}

// ─── Home Page Data ──────────────────────────────────────────────────────────

function loadHomeFromStorage(lang: string): StoredHomeEntry | null {
  try {
    const raw = localStorage.getItem(`${LS_HOME_PREFIX}${lang}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'fetchedAt' in parsed && 'data' in parsed) {
      return parsed as StoredHomeEntry;
    }
    return null;
  } catch {
    return null;
  }
}

function saveHomeToStorage(lang: string, data: HomePageData, fetchedAt: number): void {
  try {
    const entry: StoredHomeEntry = { data, fetchedAt };
    localStorage.setItem(`${LS_HOME_PREFIX}${lang}`, JSON.stringify(entry));
  } catch { /* ignore */ }
}

export function getCachedHomeData(lang: string): HomePageData | null {
  return cache.homeData.get(lang)?.data ?? null;
}

export async function fetchHomeData(lang: string): Promise<HomePageData> {
  const entry = cache.homeData.get(lang);
  if (entry && !isStale(entry)) return entry.data;
  if (homeInFlight.has(lang)) return homeInFlight.get(lang)!;

  const { loadHomePageData } = await import('../homeContent');
  const promise = loadHomePageData(lang).then((data) => {
    const fetchedAt = Date.now();
    cache.homeData.set(lang, { data, fetchedAt });
    saveHomeToStorage(lang, data, fetchedAt);
    homeInFlight.delete(lang);
    notify();
    return data;
  }).catch((e) => {
    homeInFlight.delete(lang);
    const existing = cache.homeData.get(lang)?.data;
    if (existing) return existing;
    throw e;
  });

  homeInFlight.set(lang, promise);
  return promise;
}

export function invalidateHomeDataCache(lang?: string) {
  if (lang) {
    cache.homeData.delete(lang);
    homeInFlight.delete(lang);
    try { localStorage.removeItem(`${LS_HOME_PREFIX}${lang}`); } catch { /**/ }
  } else {
    cache.homeData.clear();
    homeInFlight.clear();
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(LS_HOME_PREFIX)) toRemove.push(k);
      }
      toRemove.forEach(k => localStorage.removeItem(k));
    } catch { /**/ }
  }
}

// ─── Home CTA Buttons ─────────────────────────────────────────────────────────

export function getCachedHomeCta(lang: string) {
  return cache.homeCta.get(lang)?.data ?? null;
}

export function saveHomeCta(lang: string, data: { cta1Text: string; cta1Url: string; cta2Text: string; cta2Url: string }): void {
  const fetchedAt = Date.now();
  cache.homeCta.set(lang, { data, fetchedAt });
  try {
    localStorage.setItem(`${LS_CTA_PREFIX}${lang}`, JSON.stringify({ data, fetchedAt }));
  } catch { /**/ }
}

export function invalidateCtaCache(lang?: string) {
  if (lang) {
    cache.homeCta.delete(lang);
    try { localStorage.removeItem(`${LS_CTA_PREFIX}${lang}`); } catch { /**/ }
  } else {
    cache.homeCta.clear();
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(LS_CTA_PREFIX)) toRemove.push(k);
      }
      toRemove.forEach(k => localStorage.removeItem(k));
    } catch { /**/ }
  }
}

// ─── Initialization ──────────────────────────────────────────────────────────

(function preloadHomeFromStorage() {
  for (const lang of ALL_LANGS) {
    const entry = loadHomeFromStorage(lang);
    if (entry) {
      cache.homeData.set(lang, { data: entry.data, fetchedAt: entry.fetchedAt });
    }
  }
})();

(function preloadCtaFromStorage() {
  for (const lang of ALL_LANGS) {
    try {
      const raw = localStorage.getItem(`${LS_CTA_PREFIX}${lang}`);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed && 'data' in parsed && 'fetchedAt' in parsed) {
        cache.homeCta.set(lang, { data: parsed.data, fetchedAt: parsed.fetchedAt });
      }
    } catch { /**/ }
  }
})();
